import { useCallback, useRef, useState } from "react";

// React port of the shipped Vue composable `simply/src/composables/usePayment.js`
// (Accept.Blue hosted-tokenization + PAAY 3DS). Mirrors its logic exactly:
// idempotent SDK script injection, HostedTokenization construction, event wiring
// ('ready' / 'challenge'), getNonceToken, verify3DS, destroy.
//
// Task 4 wires this into the deposit UI — this hook only exposes the primitives.

const loadScript = (src) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) return Promise.resolve();
    return new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = () => reject(new Error(`Failed to load: ${src}`));
        document.head.appendChild(s);
    });
};

// Field-level styling for the Accept.Blue hosted-tokenization iframes. Ported verbatim from
// simply/src/composables/usePayment.js so the card number / expiry / CVV2 inputs match the
// simply form. `base` styles the text inside each iframe; `setStyles` styles the field boxes.
const CARD_BASE_STYLES = {
    base: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: "16px",
        color: "#1a1a1a",
        backgroundColor: "transparent",
        border: "none",
        padding: "14px",
    },
    focus: { color: "#000" },
    invalid: { color: "#e11d48" },
    placeholder: { color: "#a0a0a0" },
};

const DEFAULT_BORDER = "1px solid #d1d5db";
const ERROR_BORDER = "1px solid #e11d48";

const FIELD_STYLES = {
    container: `
        width: 100%;
        max-width: 100%;
        height: 100px;
    `,
    card: `
        width: 100%;
        height: 44px;
        border: ${DEFAULT_BORDER};
        border-radius: 8px;
        padding: 0 12px;
        font-size: 14px;
        box-sizing: border-box;
        margin-bottom: 12px;
        background: #fff;
        display: block;
    `,
    expiryContainer: `
    display: inline-flex;
    align-items: stretch;
    width: calc(50% - 14px);
    height: 44px;
    border: ${DEFAULT_BORDER};
    border-radius: 8px;
    box-sizing: border-box;
    margin-right: 12px;
    background: #fff;
    overflow: hidden;
`,
    cvv2: `
        display: inline-block;
        vertical-align: top;
        width: calc(50% - 8px);
        height: 44px;
        border: ${DEFAULT_BORDER};
        border-radius: 8px;
        padding: 0 12px;
        font-size: 14px;
        box-sizing: border-box;
        background: #fff;
    `,
    expiryMonth: `
        flex: 1;
        width: 100%;
        height: 100%;
        border: none;
        background: transparent;
        padding: 0 12px;
        box-sizing: border-box;
    `,
    expirySeparator: `
        width: 12px;
        flex: 0 0 12px;
        display: flex;
        align-items: center;
        justify-content: center;
    `,
    expiryYear: `
        flex: 1;
        width: 100%;
        height: 100%;
        border: none;
        padding: 0 8px;
        box-sizing: border-box;
    `,
};

// Rebuild the field styles with an error border on the offending field.
const buildFieldStyles = (errorField) => {
    const styles = { ...FIELD_STYLES };
    if (errorField) {
        styles[errorField] = styles[errorField].replace(
            `border: ${DEFAULT_BORDER}`,
            `border: ${ERROR_BORDER}`,
        );
    }
    return styles;
};

export const useAcceptBluePayment = () => {
    const [ready, setReady] = useState(false);
    const [challengeVisible, setChallengeVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [cardInputError, setCardInputError] = useState("");

    const instanceRef = useRef(null);

    const mountContainer = useCallback(async (targetSelector = "#accept-blue-container") => {
        // Destroy any previous instance and clear orphaned iframes (handles HMR re-mounts)
        try { if (instanceRef.current) instanceRef.current.destroy(); } catch {}
        instanceRef.current = null;
        const container = document.querySelector(targetSelector);
        if (container) container.innerHTML = '';

        setReady(false);
        setLoading(true);
        setError("");
        setCardInputError("");
        try {
            const sdkUrl = process.env.REACT_APP_ACCEPT_BLUE_SDK_URL;
            await loadScript(sdkUrl);

            if (!window.HostedTokenization) {
                throw new Error("Accept.Blue SDK failed to initialize");
            }

            const publicKey = process.env.REACT_APP_ACCEPT_BLUE_PK;
            if (!publicKey) {
                throw new Error("Accept.Blue public key is not configured");
            }

            const paayApiKey = process.env.REACT_APP_ACCEPT_BLUE_PAAY_KEY;
            if (!paayApiKey) {
                throw new Error("Accept.Blue 3DS (PAAY) key is not configured");
            }

            // 'test' (sandbox) | 'prod' (live) — SDK defaults to 'prod' if unset. Mirrors simply's
            // usePayment.js: sandbox test cards hard-decline on CVV2 at the processor regardless of
            // the CVV, so CVV2 is dropped in 'test' to let an authenticated charge approve.
            const acceptBlueEnv = process.env.REACT_APP_ACCEPT_BLUE_ENV === "test" ? "test" : "prod";

            const instance = new window.HostedTokenization(publicKey, {
                target: targetSelector,
                requireCvv2: acceptBlueEnv === "prod",
                threeDS: {
                    apiKey: paayApiKey,
                    env: acceptBlueEnv,
                },
                styles: CARD_BASE_STYLES,
            });

            // Style the field boxes (card / expiry / cvv2) and flag the offending field on error,
            // mirroring simply's usePayment.js so the card UI matches the simply form.
            instance.on("input", (evt) => {
                const msg = (evt && evt.error) || "";
                setCardInputError(msg);
                if (!msg) {
                    instance.setStyles(buildFieldStyles(null));
                } else if (/card number/i.test(msg)) {
                    instance.setStyles(buildFieldStyles("card"));
                } else if (/expiration/i.test(msg)) {
                    instance.setStyles(buildFieldStyles("expiryContainer"));
                } else if (/invalid cvv2/i.test(msg)) {
                    instance.setStyles(buildFieldStyles("cvv2"));
                }
            });
            instance.setStyles(FIELD_STYLES);

            instance.on("ready", () => {
                setReady(true);
            });

            instance.on("challenge", (evt) => {
                // SDK renders the ACS challenge UI itself; this only tracks visibility.
                setChallengeVisible(!!(evt && evt.visible));
            });

            instanceRef.current = instance;
        } catch (err) {
            setError(err instanceof Error ? err.message : "Payment initialization failed");
        } finally {
            setLoading(false);
        }
    }, []);

    const getNonceToken = useCallback(async () => {
        if (!instanceRef.current) {
            throw new Error("Payment SDK not initialized");
        }
        const result = await instanceRef.current.getNonceToken();
        if (result.error) {
            throw new Error(result.error);
        }
        // Task 4 handles `nonce-` prefixing + 4-digit expiry per the PAAY gotchas — these are the
        // raw SDK values.
        return {
            nonce: result.nonce,
            expiryMonth: result.expiryMonth,
            expiryYear: result.expiryYear,
        };
    }, []);

    const verify3DS = useCallback(async ({ amount, billing } = {}) => {
        if (!instanceRef.current) {
            throw new Error("Payment SDK not initialized");
        }
        // v0.3: resolves a ThreeDSResult { authenticationValue(=cavv), status, eci, dsTransId, ... }
        const result = await instanceRef.current.verify3DS({ amount, billing });
        return {
            authenticationValue: result.authenticationValue,
            eci: result.eci,
            dsTransId: result.dsTransId,
            status: result.status,
        };
    }, []);

    const destroy = useCallback(() => {
        try {
            if (instanceRef.current) {
                instanceRef.current.destroy();
            }
        } catch {
            // ignore cleanup errors
        }
        instanceRef.current = null;
        setReady(false);
        setChallengeVisible(false);
        setError("");
        setCardInputError("");
    }, []);

    return {
        mountContainer,
        getNonceToken,
        verify3DS,
        ready,
        challengeVisible,
        loading,
        error,
        cardInputError,
        destroy,
    };
};
